import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Icon from '@mui/material/Icon';
import DeleteIcon from '@mui/icons-material/Delete';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { OnpContext } from './OnpLayout';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getItem as getItemUtils, computeTotal as computeTotalUtils } from './utils';
import { translate } from '../../i18n/customI18nProvider';

/**
 * Mobile bottom navigation: Consommation | Panier | Commandes
 * - fixed to bottom, full width
 * - semi-transparent background with blur (backdrop-filter)
 * - evenly spaced actions
 */
export default function OnpNavigation() {
  const { barInfo, order, clearOrder, consumptionChange, maxWidth, navigate } = React.useContext(OnpContext) || {};
  const { barId, table_number } = useParams();
  const history = useHistory();
  const location = useLocation();

  let value = 'consumptions';
  if (location.pathname.endsWith('/cart') || location.pathname.endsWith('/twint') || location.pathname.endsWith('/twint_direct') || location.pathname.endsWith('/card')) value = 'cart';
  else if (location.pathname.endsWith('/orders')) value = 'orders';

  const handleClick = (newValue) => {
    if (onChange) onChange(newValue);
  };

  const iconColor = (item) => (value === item ? 'black' : 'gray');

  const getItem = (id) => getItemUtils(barInfo, id);

  const computeTotal = () => computeTotalUtils(order, barInfo);

  const onNavigate = (newValue) => {
    let path = '/onp/' + barId + '/' + table_number + '/';
    if (newValue === 'consumptions') path += '';
    else if (newValue === 'cart') path += 'cart';
    else if (newValue === 'orders') path += 'orders';
    else return;

    navigate(path);
  }

  const cartCount = order.reduce((sum, item) => {
    const consumption = getItem(item.id);
    if (consumption?.type === 'input') {
      return sum + 1;
    }
    return sum + item.quantity;
  }, 0);

  const total = computeTotal();
  const fmt = (n) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(n);

  return (
    <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, maxWidth: maxWidth || '100%', margin: 'auto', zIndex: 1400 }}>
      {/* single Paper that contains summary (if any) stacked above the icon nav */}
      <Paper
        elevation={0}
        sx={{
          mx: 0,
          mb: 0,
          px: 0,
          py: 0,
          width: '100%',
          maxWidth: '100%',
          background: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderRadius: 0,
        }}
      >

        {(total > 0 && value == 'consumptions') && (
          <Box sx={{ px: 1, py: 2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <Typography variant="subtitle1" sx={{fontSize: 22, fontWeight: 700 }}>{fmt(total)}</Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => (onNavigate ? onNavigate('cart') : null)}
              sx={{ minWidth: 120, px: 2, py: 0.5, fontWeight: 700, fontSize: 18 }}
            >
              {translate('custom.payment')}
            </Button>
          </Box>
        )}

        <Box sx={{ px: 2, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
            <Tooltip title="Consommation">
              <IconButton aria-label="Consommation" onClick={() => (onNavigate ? onNavigate('consumptions') : handleClick('consumptions'))} sx={{ color: iconColor('consumptions') }} size="large">
                <LocalBarIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Panier">
              <IconButton aria-label="Panier" onClick={() => (onNavigate ? onNavigate('cart') : handleClick('cart'))} sx={{ color: iconColor('cart') }} size="large">
                <Badge badgeContent={cartCount} color="primary" invisible={cartCount <= 0}>
                  <ShoppingCartIcon fontSize="inherit" />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Commandes">
              <IconButton aria-label="Commandes" onClick={() => (onNavigate ? onNavigate('orders') : handleClick('orders'))} sx={{ color: iconColor('orders') }} size="large">
                <ListAltIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography align="center" sx={{fontSize: 10, color: 'text.secondary', width: '100%' }}>
            Order & Pay - <a href="https://jm-contactless.ch">JM Contactless</a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

OnpNavigation.propTypes = {
  value: PropTypes.oneOf(['consumptions', 'cart', 'orders']),
  onChange: PropTypes.func,
  cartCount: PropTypes.number,
  order: PropTypes.array,
  barInfo: PropTypes.object,
  onPay: PropTypes.func,
  onClear: PropTypes.func,
};
